import { CSSProperties, FC } from 'react';
import { defineMessages, useIntl } from 'dibs-react-intl';
import classnames from 'classnames';
import { useFragment, graphql } from 'react-relay';

import { OriginalListPrice_item$key } from './__generated__/OriginalListPrice_item.graphql';

import styles from './styles.scss';
import { useOriginalListPrice } from '../../hooks/useOriginalListPrice';

export const PUB_TOOLS_KEY = 'pubTools';
const SEARCH_BROWSE_KEY = 'searchBrowse';
const EMAIL_KEY = 'email';

const message = defineMessages({
    [SEARCH_BROWSE_KEY]: {
        id: 'dibs-search-product-tile.Price.OriginalListPrice.searchBrowse',
        defaultMessage: `{isCompact, select,
            true {{price} Orig. List}
            other {{price} Original List Price}
        }`,
    },
    [EMAIL_KEY]: {
        id: 'dibs-search-product-tile.Price.OriginalListPrice.email',
        defaultMessage: 'Original List Price: {price}',
    },
    [PUB_TOOLS_KEY]: {
        id: 'dibs-search-product-tile.Price.OriginalListPrice.pubTools',
        defaultMessage: 'Orig. List Price: {price}',
    },
});

const itemFragment = graphql`
    fragment OriginalListPrice_item on Item
    @argumentDefinitions(page: { type: "PageDisplayEnum", defaultValue: pdp }) {
        ...useOriginalListPrice_item @arguments(page: $page)
    }
`;

export type OriginalListPriceProps = {
    currency: string;
    item: OriginalListPrice_item$key | null;
    isCompact?: boolean;
    className?: string;
    type?: typeof SEARCH_BROWSE_KEY | typeof EMAIL_KEY | typeof PUB_TOOLS_KEY;
    style?: CSSProperties;
};

const OriginalListPrice: FC<OriginalListPriceProps> = ({
    currency,
    item: itemRef,
    isCompact,
    className,
    type = SEARCH_BROWSE_KEY,
    style,
}) => {
    const intl = useIntl();
    const item = useFragment(itemFragment, itemRef);
    const originalListPrice = useOriginalListPrice({ item, currency });

    if (!originalListPrice) {
        return null;
    }

    const messageToFormat = message[type];
    const priceIntl = intl.formatNumber(originalListPrice, {
        currency,
        style: 'currency',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    });

    return (
        <span
            className={classnames(styles.originalListPrice, className)}
            style={style}
            data-tn="original-list-price"
        >
            {intl.formatMessage(messageToFormat, {
                price: priceIntl,
                isCompact,
            })}
        </span>
    );
};

export default OriginalListPrice;
